"use client";

import { ChevronDown, ChevronUp } from "lucide-react";
import { GradientProgressBar } from "@/components/ui/gradient-progress-bar";
import type {
  GettingStartedParentId,
  GettingStartedTask,
} from "@/constants/getting-started";
import type { GettingStartedTaskId } from "@jurni/schemas";
import {
  GettingStartedTaskRow,
  type GettingStartedTaskStatus,
} from "./getting-started-task-row";

export interface GettingStartedSectionData {
  id: GettingStartedParentId;
  label: string;
  percentage: number;
  isCollapsed: boolean;
  tasks: GettingStartedTask[];
}

interface Props {
  section: GettingStartedSectionData;
  taskStatusMap: Map<GettingStartedTaskId, GettingStartedTaskStatus>;
  onToggle: (sectionId: GettingStartedParentId) => void;
  onTaskClick: (task: GettingStartedTask) => void;
  onDismissTask: (taskId: GettingStartedTaskId) => void;
}

export const GettingStartedSection = ({
  section,
  taskStatusMap,
  onToggle,
  onTaskClick,
  onDismissTask,
}: Props) => (
  <div>
    <button
      type="button"
      onClick={() => onToggle(section.id)}
      className="flex w-full items-center justify-between py-4 text-left"
    >
      <div className="flex items-center gap-1">
        <span className="text-[13px] font-medium tracking-[-0.13px] text-foreground">
          {section.label}
        </span>
        <span className="text-[13px] tracking-[-0.13px] text-muted-foreground">
          {section.percentage}%
        </span>
      </div>
      <div className="flex items-center gap-4">
        <GradientProgressBar percentage={section.percentage} />
        <span className="text-muted-foreground">
          {section.isCollapsed ? (
            <ChevronDown size={14} strokeWidth={2} />
          ) : (
            <ChevronUp size={14} strokeWidth={2} />
          )}
        </span>
      </div>
    </button>

    {!section.isCollapsed && (
      <div className="flex flex-col rounded-md bg-muted p-2">
        {section.tasks.map((task) => (
          <GettingStartedTaskRow
            key={task.id}
            task={task}
            status={taskStatusMap.get(task.id)}
            onClick={() => onTaskClick(task)}
            onDismiss={() => onDismissTask(task.id)}
          />
        ))}
      </div>
    )}
  </div>
);
